
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { ArrowRight, Droplets, Battery, Shield, Wifi, Cpu, HardDrive, Clock, Award, Users, Building, GraduationCap, Factory } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, useNavigate } from "react-router-dom";

const ProductTablet = () => {
  const navigate = useNavigate();

  const handleRequestQuote = () => {
    navigate('/', { state: { scrollToContact: true } });
  };

  // Enhanced structured data for product with all required fields
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': 'Custom Industrial Android Tablets - 25+ Years Manufacturing Excellence',
    'image': [
      'https://truecoregadgets.com/lovable-uploads/db06e8f0-c84f-4067-95e8-1637cc01e1fc.png',
      'https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=600&auto=format&fit=crop'
    ],
    'description': 'Premium ruggedized Android tablets manufactured in India with 25+ years of hardware expertise. IP68 waterproof, dustproof, enterprise security & extended battery for industrial applications.',
    'brand': {
      '@type': 'Brand',
      'name': 'Truecore Gadgets',
      'foundingDate': '2000'
    },
    'manufacturer': {
      '@type': 'Organization',
      'name': 'Truecore Gadgets',
      'foundingDate': '2000',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Mumbai',
        'addressRegion': 'Maharashtra',
        'addressCountry': 'India'
      }
    },
    'offers': {
      '@type': 'Offer',
      'price': '45000',
      'priceCurrency': 'INR',
      'priceValidUntil': '2025-12-31',
      'availability': 'https://schema.org/InStock',
      'seller': {
        '@type': 'Organization',
        'name': 'Truecore Gadgets - 25+ Years Manufacturing Excellence'
      }
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.8',
      'reviewCount': '127',
      'bestRating': '5',
      'worstRating': '1'
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Best Custom Android Tablets India | 25+ Years Manufacturing | Truecore Gadgets</title>
        <meta name="description" content="India's #1 custom Android tablet manufacturer with 25+ years experience. Waterproof IP68 tablets, enterprise security, extended battery. Trusted by 500+ companies. Order online now!" />
        <meta name="keywords" content="custom android tablets India, buy tablets online India, industrial tablets manufacturer, ruggedized tablets India, waterproof tablets, enterprise tablets, custom tablet manufacturer Mumbai, tablet manufacturer 25 years experience, IP68 tablets India, business tablets India, field tablets, custom hardware manufacturer India, android POS tablets, barcode scanner tablets, warehouse tablets India" />
        
        <link rel="canonical" href="https://truecoregadgets.com/products/tablets" />
        
        <meta property="og:type" content="product" />
        <meta property="og:title" content="Best Custom Android Tablets India | 25+ Years Manufacturing Excellence" />
        <meta property="og:description" content="Premium IP68 ruggedized Android tablets by India's most experienced manufacturer. 25+ years of hardware expertise. Waterproof, dustproof, enterprise-grade. Order today!" />
        <meta property="og:url" content="https://truecoregadgets.com/products/tablets" />
        <meta property="og:image" content="https://truecoregadgets.com/lovable-uploads/db06e8f0-c84f-4067-95e8-1637cc01e1fc.png" />
        
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      </Helmet>

      <section className="container mx-auto py-16 px-4 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 gradient-text">Custom Android Tablets - Industrial Grade Excellence</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Built tough for enterprise environments. 25+ years of manufacturing expertise delivers tablets that work where others fail.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mb-8">
            <span className="flex items-center"><Shield className="h-4 w-4 mr-2 text-primary" />IP68 Waterproof</span>
            <span className="flex items-center"><Battery className="h-4 w-4 mr-2 text-primary" />14+ Hour Battery</span>
            <span className="flex items-center"><Award className="h-4 w-4 mr-2 text-primary" />5-Year Warranty</span>
            <span className="flex items-center"><Clock className="h-4 w-4 mr-2 text-primary" />Fast Delivery</span>
          </div>
        </div>

        {/* Main Product Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <img
              src="/lovable-uploads/db06e8f0-c84f-4067-95e8-1637cc01e1fc.png"
              className="rounded-xl shadow-lg w-full object-cover"
              alt="Professional Android Tablet - Enterprise-grade for business applications"
              loading="lazy"
            />
          </div>
          
          <div>
            <h2 className="text-3xl font-bold mb-6">Why Choose Truecore Tablets?</h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Droplets className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">IP68 Rugged Protection</h3>
                  <p className="text-muted-foreground">Fully waterproof and dustproof. Tested to military standards for extreme conditions.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Battery className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Extended Battery Life</h3>
                  <p className="text-muted-foreground">Up to 14 hours continuous operation with hot-swappable battery options.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Enterprise Security</h3>
                  <p className="text-muted-foreground">Hardware encryption, secure boot, and enterprise device management compatibility.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Wifi className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Advanced Connectivity</h3>
                  <p className="text-muted-foreground">5G, WiFi 6, Bluetooth 5.2, NFC, and optional satellite communication.</p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <Button 
                onClick={handleRequestQuote}
                className="w-full sm:w-auto flex items-center justify-center"
                size="lg"
              >
                Request Custom Quote
                <ArrowRight className="ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Detailed Tabs Section */}
        <Tabs defaultValue="specifications" className="mb-16">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="specifications">Specifications</TabsTrigger>
            <TabsTrigger value="use-cases">Use Cases</TabsTrigger>
            <TabsTrigger value="models">Models</TabsTrigger>
            <TabsTrigger value="support">Support</TabsTrigger>
          </TabsList>
          
          <TabsContent value="specifications" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Hardware Specifications</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Processor</span>
                    <span>Qualcomm Snapdragon 8 Gen 2 / MediaTek Dimensity 9200</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">RAM</span>
                    <span>8GB / 12GB / 16GB LPDDR5</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Storage</span>
                    <span>128GB / 256GB / 512GB / 1TB UFS 4.0</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Display</span>
                    <span>10.1" / 12.3" IPS LCD, 2K Resolution</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">OS</span>
                    <span>Android 13 with 5-year security updates</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Durability & Connectivity</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Protection</span>
                    <span>IP68, MIL-STD-810H</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Drop Test</span>
                    <span>1.8m multiple angle drops</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Temperature</span>
                    <span>-20°C to +60°C operating</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Connectivity</span>
                    <span>5G, WiFi 6E, Bluetooth 5.3, NFC</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ports</span>
                    <span>USB-C, 3.5mm, microSD, SIM</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="use-cases" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-card p-6 rounded-lg border text-center">
                <Building className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Enterprise</h3>
                <p className="text-sm text-muted-foreground">Field service, inventory management, digital signatures, mobile offices</p>
              </div>
              <div className="bg-card p-6 rounded-lg border text-center">
                <Factory className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Manufacturing</h3>
                <p className="text-sm text-muted-foreground">Quality control, production monitoring, safety inspections, maintenance logs</p>
              </div>
              <div className="bg-card p-6 rounded-lg border text-center">
                <GraduationCap className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Education</h3>
                <p className="text-sm text-muted-foreground">Digital classrooms, student assessment, interactive learning, remote education</p>
              </div>
              <div className="bg-card p-6 rounded-lg border text-center">
                <Users className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Healthcare</h3>
                <p className="text-sm text-muted-foreground">Patient records, medical imaging, bedside care, telemedicine applications</p>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="models" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">TrueTab Lite</h3>
                <div className="text-2xl font-bold text-primary mb-2">₹35,000</div>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  <li>• 10.1" Display</li>
                  <li>• 8GB RAM, 128GB Storage</li>
                  <li>• IP65 Protection</li>
                  <li>• 10-hour battery</li>
                  <li>• WiFi + 4G</li>
                </ul>
                <Button onClick={handleRequestQuote} variant="outline" className="w-full">Request Quote</Button>
              </div>
              
              <div className="bg-card p-6 rounded-lg border ring-2 ring-primary">
                <div className="text-xs bg-primary text-white px-2 py-1 rounded mb-2 inline-block">MOST POPULAR</div>
                <h3 className="text-xl font-semibold mb-4">TrueTab Pro</h3>
                <div className="text-2xl font-bold text-primary mb-2">₹55,000</div>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  <li>• 12.3" 2K Display</li>
                  <li>• 12GB RAM, 256GB Storage</li>
                  <li>• IP68 Protection</li>
                  <li>• 14-hour battery</li>
                  <li>• WiFi + 5G</li>
                </ul>
                <Button onClick={handleRequestQuote} className="w-full">Request Quote</Button>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">TrueTab Max</h3>
                <div className="text-2xl font-bold text-primary mb-2">₹75,000</div>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  <li>• 12.3" 2K Display</li>
                  <li>• 16GB RAM, 512GB Storage</li>
                  <li>• IP68 + MIL-STD</li>
                  <li>• 16-hour battery</li>
                  <li>• WiFi + 5G + Satellite</li>
                </ul>
                <Button onClick={handleRequestQuote} variant="outline" className="w-full">Request Quote</Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="support" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4">Warranty & Support</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Shield className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">5-Year Comprehensive Warranty</div>
                      <div className="text-sm text-muted-foreground">Hardware defects, software support, and replacement coverage</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Clock className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">24/7 Technical Support</div>
                      <div className="text-sm text-muted-foreground">Phone, email, and remote assistance available round the clock</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Award className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">On-site Service</div>
                      <div className="text-sm text-muted-foreground">Field engineer visits for enterprise customers</div>
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-4">Customization Services</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Cpu className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">Hardware Customization</div>
                      <div className="text-sm text-muted-foreground">Custom ports, sensors, cameras, and mounting solutions</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <HardDrive className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">Software Integration</div>
                      <div className="text-sm text-muted-foreground">Pre-installed enterprise apps and custom OS configurations</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Users className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">Training & Setup</div>
                      <div className="text-sm text-muted-foreground">Staff training and deployment assistance for large orders</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Customer Reviews */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Customer Success Stories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ))}
                </div>
                <span className="ml-2 text-gray-600 text-sm">5.0</span>
              </div>
              <p className="text-gray-700 mb-4">"These tablets have revolutionized our field operations. The durability is exceptional - they've survived drops, water exposure, and extreme temperatures. Our productivity has increased by 40%."</p>
              <div className="text-sm">
                <p className="font-medium">Rajesh Kumar</p>
                <p className="text-gray-500">Operations Manager, Logistics Company</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ))}
                </div>
                <span className="ml-2 text-gray-600 text-sm">5.0</span>
              </div>
              <p className="text-gray-700 mb-4">"Truecore customized our tablets with specific software and hardware configurations. Battery life is impressive and the after-sales support has been excellent. Best investment we've made."</p>
              <div className="text-sm">
                <p className="font-medium">Ananya Sharma</p>
                <p className="text-gray-500">IT Director, Healthcare Provider</p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16 bg-muted/30 px-8 py-10 rounded-xl">
          <h2 className="text-2xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-semibold text-primary mb-2">Can you install our company's apps?</h3>
              <p className="text-muted-foreground text-sm mb-4">Yes, we can pre-install your enterprise applications and configure them according to your requirements.</p>
              
              <h3 className="font-semibold text-primary mb-2">What security features are included?</h3>
              <p className="text-muted-foreground text-sm mb-4">Hardware encryption, secure boot, biometric authentication, and enterprise mobile device management compatibility.</p>
            </div>
            <div>
              <h3 className="font-semibold text-primary mb-2">How rugged are these tablets?</h3>
              <p className="text-muted-foreground text-sm mb-4">Built to MIL-STD-810H standards with IP68 protection, tested for 1.8m drops and extreme temperature variations.</p>
              
              <h3 className="font-semibold text-primary mb-2">What about bulk order discounts?</h3>
              <p className="text-muted-foreground text-sm">We offer significant discounts for orders over 50 units, plus free deployment support and extended warranties.</p>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Complete Your Technology Setup</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-2">Custom PCs & Workstations</h3>
              <p className="text-muted-foreground mb-4">
                Powerful desktop solutions to complement your tablet deployment for office and processing needs.
              </p>
              <Link to="/products/pcs" className="text-primary flex items-center">
                Explore PCs
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-2">Smart Gadgets & Accessories</h3>
              <p className="text-muted-foreground mb-4">
                Complementary devices like barcode scanners, docking stations, and IoT sensors for complete solutions.
              </p>
              <Link to="/products/smart-gadgets" className="text-primary flex items-center">
                Explore Smart Gadgets
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center bg-gradient-to-r from-primary/5 to-secondary/5 p-8 rounded-xl">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Operations?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Join 500+ companies who trust Truecore for their tablet solutions. Get a custom quote based on your specific requirements and quantity.
          </p>
          <Button 
            onClick={handleRequestQuote}
            size="lg"
            className="text-lg px-8 py-3"
          >
            Request Custom Quote - Free Consultation
            <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ProductTablet;
